import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../../Environments/environment";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import { apiClient } from "../../../api";
import { Switch } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const UpdateProperty = () => {
  const [propertyData, setPropertyData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    website: "",
    about: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zipcode: "",
    countryCode: "",
    logo: null,
    cover: null,
    standardCheckinTime: "",
    standardCheckoutTime: "",
    timezone: "",
    defaultNewDayTime: "",
    notificationSoundEnabled: false,
  });
  const { selectedPropertyId } = useProperty();
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedPropertyId) {
      fetchPropertyData(selectedPropertyId);
    }
  }, [selectedPropertyId]);

  const fetchPropertyData = async (propertyId) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/property/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          withCredentials: true,
        },
      );

      if (response.status === 200) {
        const { property, guestPropertyMobile } = response.data.result.property;
        const { setting } = response.data.result;

        setPropertyData({
          name: property.name || "",
          email: property.email || "",
          phoneNumber: property.phoneNumber || "",
          website: property.website || "",
          about: property.about || "",
          country: property.country || "",
          state: property.state || "",
          city: property.city || "",
          address: property.address || "",
          zipcode: property.zipcode || "",
          countryCode: property.countryCode || "",
          logo: property.logoUrl || null,
          cover: property.coverUrl || null,
          guestPropertyMobile: guestPropertyMobile.phoneNumber || "",
          guestPropertyCountryCode: guestPropertyMobile.countryCode || "",
          standardCheckinTime: setting.standardCheckinTime || "",
          standardCheckoutTime: setting.standardCheckoutTime || "",
          timezone: setting.timezone || "",
          defaultNewDayTime: setting.defaultNewDayTime || "",
          notificationSoundEnabled: setting.notificationSoundEnabled || false,
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error fetching property data.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Create a FormData object to handle file uploads
      const formData = new FormData();

      // Append the propertyData fields to the formData
      for (const key in propertyData) {
        if (key !== "logo" && key !== "cover") {
          formData.append(key, propertyData[key]);
        }
      }

      // Append the logo and cover files to the formData
      if (propertyData.logo instanceof File) {
        formData.append("logo", propertyData.logo);
      }
      if (propertyData.cover instanceof File) {
        formData.append("cover", propertyData.cover);
      }

      // Send the PUT request with formData
      const response = await apiClient.put(
        `${baseUrl}/property/${selectedPropertyId}`,
        formData, // Send the FormData object
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
          withCredentials: true,
        },
      );

      if (response.status === 200) {
        toast.success("Property updated successfully.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error updating property.";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="relative bg-white rounded-lg shadow- p-6 w-full max-w-7xl mx-auto h-auto">
      {/* Modal Header */}
      <div className="px-4 sm:px-6 lg:px-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Property Details
        </h2>
        <div className="flex items-center gap-2">
		{loading ? (
					<p></p>
				) : (

					<div className="text-xs sm:text-sm text-gray-500 pr-3 flex items-center gap-2 group relative flex-wrap">
					<label className="text-gray-800 font-semibold">
					  Notification Sound:
					</label>
		
					{/* Tooltip Icon */}
					<InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-indigo-600 cursor-pointer flex-shrink-0" />
		
					{/* Tooltip box */}
					<div
					  className="absolute left-0 top-6 z-10
				hidden group-hover:block
				bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max max-w-[200px] sm:max-w-none"
					>
					  Enable notification sound for new messages & add ons
					  notifications.
					</div>
		
					<Switch
					  checked={propertyData?.notificationSoundEnabled}
					  onChange={(value) =>
						setPropertyData({
						  ...propertyData,
						  notificationSoundEnabled: value,
						})
					  }
					  className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer
				rounded-full border-2 border-transparent bg-gray-300 transition-colors duration-200 ease-in-out
				focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
				data-[checked]:bg-indigo-600"
					>
					  <span className="sr-only">Enable notification sound</span>
					  <span
						aria-hidden="true"
						className="pointer-events-none inline-block h-5 w-5 transform rounded-full 
				  bg-white shadow ring-0 transition duration-200 ease-in-out 
				  group-data-[checked]:translate-x-5"
					  />
					</Switch>
				  </div>	)}
         
          <button
            className="bg-green-400 text-white px-4 py-2 rounded-md font-medium hover:bg-green-500"
            onClick={handleSaveChanges}
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Property Form */}
      <div className="px-4 sm:px-6 lg:px-4 mt-8">
        {loading ? (
          <p>Loading property details...</p>
        ) : (
          <form className="grid grid-cols-2 gap-6">
            {/* Property Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Property Name
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter property name"
                value={propertyData.name}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, name: e.target.value })
                }
              />
            </div>

            {/* Property Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Property Email
              </label>
              <input
                type="email"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter property email"
                value={propertyData.email}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, email: e.target.value })
                }
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="flex">
                <select
                  value={propertyData.countryCode}
                  onChange={(e) =>
                    setPropertyData({
                      ...propertyData,
                      countryCode: e.target.value,
                    })
                  }
                  className="rounded-l-md border-r-0 border-gray-300 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="+1">+1</option>
                  <option value="+91">+91</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  Add more country codes as needed
                </select>
                <input
                  type="tel"
                  className="block w-full rounded-r-md border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Enter phone number"
                  value={propertyData.phoneNumber}
                  onChange={(e) =>
                    setPropertyData({
                      ...propertyData,
                      phoneNumber: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Property Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Property Website
              </label>
              <input
                type="url"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter property website"
                value={propertyData.website}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    website: e.target.value,
                  })
                }
              />
            </div>

            {/* About Property */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                About the Property
              </label>
              <textarea
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                rows="3"
                placeholder="Write something about the property"
                value={propertyData.about}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, about: e.target.value })
                }
              />
            </div>

            {/* Logo photo */}
            <div className="col-span-1">
              <label
                htmlFor="Logo"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Logo
              </label>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                <div className="text-center">
                  {propertyData.logo ? (
                    <img
                      src={
                        propertyData.logo instanceof File
                          ? URL.createObjectURL(propertyData.logo)
                          : propertyData.logo
                      }
                      alt="Logo"
                      className="h-24 w-24 object-cover"
                    />
                  ) : (
                    <PhotoIcon
                      aria-hidden="true"
                      className="mx-auto h-10 w-10 text-gray-300"
                    />
                  )}
                  {/* Upload input */}
                  <label
                    htmlFor="logo-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span className="text-right">Upload a file</span>
                    <input
                      id="logo-upload"
                      name="logo-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (e.target.files[0].size > 10 * 1024 * 1024) {
                          alert("Please upload a file smaller than 10MB");
                          return;
                        }
                        setPropertyData({
                          ...propertyData,
                          logo: file,
                        });
                      }}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-xs leading-5 text-gray-600">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>
            </div>

            {/* Cover photo */}
            <div className="col-span-1">
              <label
                htmlFor="cover-photo"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Cover photo
              </label>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                <div className="text-center">
                  {propertyData.cover ? (
                    <img
                      src={
                        propertyData.cover instanceof File
                          ? URL.createObjectURL(propertyData.cover)
                          : propertyData.cover
                      }
                      alt="Cover"
                      className="h-32 w-32 object-cover"
                    />
                  ) : (
                    <PhotoIcon
                      aria-hidden="true"
                      className="mx-auto h-10 w-10 text-gray-300"
                    />
                  )}
                  {/* Upload input */}
                  <label
                    htmlFor="cover-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span className="text-right">Upload a file</span>
                    <input
                      id="cover-upload"
                      name="cover-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (e.target.files[0].size > 10 * 1024 * 1024) {
                          alert("Please upload a file smaller than 10MB");
                          return;
                        }
                        setPropertyData({
                          ...propertyData,
                          cover: file, // Store the file object
                        });
                      }}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-xs leading-5 text-gray-600">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter country"
                value={propertyData.country}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    country: e.target.value,
                  })
                }
              />
            </div>

            {/* Zip/Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Zip/Postal Code
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter zip or postal code"
                value={propertyData.zipcode}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    zipcode: e.target.value,
                  })
                }
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                State/Province
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter state or province"
                value={propertyData.state}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, state: e.target.value })
                }
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter city"
                value={propertyData.city}
                onChange={(e) =>
                  setPropertyData({ ...propertyData, city: e.target.value })
                }
              />
            </div>

            {/* Property Address */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Property Address
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter property address"
                value={propertyData.address}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    address: e.target.value,
                  })
                }
              />
            </div>

            {/* Standard Check-in Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Standard Check-in Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter standard check-in time"
                value={propertyData.standardCheckinTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    standardCheckinTime: e.target.value,
                  })
                }
              />
            </div>

            {/* Standard Check-out Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Standard Check-out Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter standard check-out time"
                value={propertyData.standardCheckoutTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    standardCheckoutTime: e.target.value,
                  })
                }
              />
            </div>

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Timezone
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter timezone"
                value={propertyData.timezone}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    timezone: e.target.value,
                  })
                }
              />
            </div>

            {/* Default New Day Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Default New Day Time
              </label>
              <input
                type="time"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter default new day time"
                value={propertyData.defaultNewDayTime}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    defaultNewDayTime: e.target.value,
                  })
                }
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UpdateProperty;
